/**
 * @file Dynamic form-field rendering helpers.
 */

import {
    DYNAMIC_HEADING_CLASSES,
    FIELD_WRAPPER_CLASSES,
    FIXED_TEXTAREA_CLASSES,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    NATIVE_INPUT_CLASSES,
    bindNativeFieldBehavior,
    getFieldLabelHtml,
} from "./nativeFieldStyles.js";
import { createSelectControl } from "../ui/controls/selectControl.js";

function attrValue(value = '') {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function getDynamicFieldName(fieldConfig) {
    const label = fieldConfig?.label || 'Field';
    return fieldConfig?.name || `custom_${label.replace(/\s+/g, '_').toLowerCase()}`;
}

/**
 * Resolves the visible label text for a rendered dynamic field control.
 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input Dynamic field control.
 * @param {string} [fallbackName=''] Generated field name fallback.
 * @returns {string} Visible field label.
 */
export function getDynamicInputLabelText(input, fallbackName = '') {
    const wrappedLabel = input.closest?.('label');
    if (wrappedLabel?.contains(input)) {
        const inlineLabel = wrappedLabel.querySelector(
            ':scope > span:not([data-native-checkbox-marker])',
        );
        const inlineText = inlineLabel?.textContent?.replace('*', '').trim();
        if (inlineText) {return inlineText;}

        const labelClone = wrappedLabel.cloneNode(true);
        labelClone
            .querySelectorAll('input, [data-native-checkbox-marker]')
            .forEach((element) => element.remove());
        const wrappedText = labelClone.textContent?.replace('*', '').trim();
        if (wrappedText) {return wrappedText;}
    }

    const parent = input.parentElement;
    const labelEl = parent ? parent.querySelector('label') : null;
    const labelText = labelEl?.textContent?.replace('*', '').trim();
    if (labelText) {return labelText;}

    return fallbackName.replace(/_/g, ' ').replace(/\b\w/g, (letter) =>
        letter.toUpperCase());
}

function getDynamicSelectOptions(fieldConfig) {
    return (Array.isArray(fieldConfig?.options) ? fieldConfig.options : [])
        .map((option) => {
            if (option && typeof option === 'object') {
                const value = option.value ?? option.id ?? option.name ?? option.label ?? '';
                return {
                    value: String(value),
                    label: String(option.label ?? option.name ?? value),
                };
            }
            return {
                value: String(option ?? ''),
                label: String(option ?? ''),
            };
        })
        .filter((option) => option.value !== '');
}

/**
 * Generates HTML for a single dynamic field based on configuration.
 * @param {object} fieldConfig Field configuration.
 * @returns {string} Dynamic field HTML.
 */
export function getDynamicFieldHtml(fieldConfig) {
    if (!fieldConfig) {return '';}
    const label = fieldConfig.label || 'Field';
    const name = getDynamicFieldName(fieldConfig);
    const nameAttr = attrValue(name);
    const type = fieldConfig.type || 'text';
    const required = fieldConfig.required ? 'required' : '';
    const placeholderText = attrValue(fieldConfig.placeholder || '');

    let inputHtml;
    let wrapperClass = 'col-span-1 md:col-span-2';

    if (type === 'heading') {
        return `<div class="${DYNAMIC_HEADING_CLASSES}">${label}</div>`;
    }

    if (type === 'select') {
        inputHtml = `
            <input type="hidden" name="${nameAttr}" value="" data-dynamic-select-mirror>
            <div data-dynamic-select-root></div>
        `;
    } else if (type === 'textarea' || type === 'long_text') {
        inputHtml = `<textarea name="${nameAttr}" class="${FIXED_TEXTAREA_CLASSES}" placeholder="${placeholderText}" ${required}></textarea>`;
        wrapperClass = 'col-span-1 md:col-span-4';
    } else if (type === 'yes_no' || type === 'checkbox' || type === 'boolean') {
        wrapperClass = 'col-span-1 md:col-span-4';
        inputHtml = `
            <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
                <input type="checkbox" name="${nameAttr}" id="field_${nameAttr}" class="${NATIVE_CHECKBOX_CLASSES}">
                <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                    <span class="${NATIVE_CHECKBOX_ICON_CLASSES}" data-native-checkbox-icon>check</span>
                </span>
                <span class="min-w-0 break-words leading-tight">${label}</span>
            </label>
        `;
    } else if (type === 'number') {
        inputHtml = `<input type="number" name="${nameAttr}" class="${NATIVE_INPUT_CLASSES}" placeholder="${placeholderText}" ${required}>`;
    } else if (type === 'date' || type === 'datetime-local') {
        inputHtml = `<input type="${type}" name="${nameAttr}" class="${NATIVE_INPUT_CLASSES}" placeholder="${placeholderText || 'dd/mm/yyyy'}" ${required}>`;
    } else {
        inputHtml = `<input type="${type}" name="${nameAttr}" class="${NATIVE_INPUT_CLASSES}" placeholder="${placeholderText}" ${required}>`;
    }

    const labelHtml = (type !== 'yes_no' && type !== 'checkbox' && type !== 'boolean')
        ? getFieldLabelHtml(label, Boolean(required))
        : '';

    return `
        <div class="${wrapperClass} ${FIELD_WRAPPER_CLASSES}">
            ${labelHtml}
            ${inputHtml}
        </div>
    `;
}

/**
 * Mounts upgraded select controls for dynamic select field placeholders.
 * @param {HTMLElement} container Dynamic field container.
 * @param {object} typeConfig Type configuration containing dynamic fields.
 * @returns {void}
 */
export function mountDynamicSelectControls(container, typeConfig) {
    const selectFields = (typeConfig?.fields || [])
        .filter((field) => field?.type === 'select');
    const roots = Array.from(container.querySelectorAll('[data-dynamic-select-root]'));

    selectFields.forEach((fieldConfig, index) => {
        const root = roots[index];
        const mirrorInput = root?.parentElement?.querySelector?.(
            'input[data-dynamic-select-mirror]',
        );
        if (!root || !mirrorInput) {return;}

        createSelectControl({
            root,
            name: getDynamicFieldName(fieldConfig),
            options: getDynamicSelectOptions(fieldConfig),
            value: mirrorInput.value || '',
            placeholder: fieldConfig.placeholder || 'Select',
            searchable: getDynamicSelectOptions(fieldConfig).length > 6,
            required: Boolean(fieldConfig.required),
            mirrorInput,
            accessibleName: fieldConfig.label || getDynamicFieldName(fieldConfig),
        });
    });
}

/**
 * Applies placeholder-style date coloring to a date input.
 * @param {HTMLInputElement} input Date input.
 * @returns {void}
 */
export function bindDatePlaceholderColor(input) {
    const updateColor = () => {
        if (!input.value) {
            input.classList.add('text-gray-500', 'dark:text-gray-400');
            input.classList.remove('text-gray-700', 'text-gray-900', 'dark:text-gray-200');
        } else {
            input.classList.remove('text-gray-500', 'dark:text-gray-400');
            input.classList.add('text-gray-700', 'dark:text-gray-200');
        }
    };
    input.addEventListener('change', updateColor);
    input.addEventListener('input', updateColor);
    updateColor();
}

/**
 * Renders dynamic fields for a selected type.
 * @param {object} typeConfig Type configuration.
 * @param {HTMLFormElement} itemForm Active form.
 * @returns {void}
 */
export function renderDynamicFieldForm(typeConfig, itemForm) {
    const anchor = itemForm.querySelector('#dynamic-fields-anchor');
    if (!anchor || !typeConfig?.fields || typeConfig.fields.length === 0) {return;}

    if (document.getElementById(`dynamic-fields-${typeConfig.id}`)) {return;}

    const container = document.createElement('div');
    container.id = `dynamic-fields-${typeConfig.id}`;
    container.className = 'md:col-span-2 p-4 bg-blue-100/40 dark:bg-blue-900/20 rounded-lg animate-fade-in';

    const itemType = itemForm.dataset.type || '';
    const itemTypeDisplay = itemType
        ? itemType.charAt(0).toUpperCase() + itemType.slice(1).replace(/_/g, ' ')
        : '';
    const headerText = `${typeConfig.name} ${itemTypeDisplay} Specifics`;

    container.innerHTML = `
        <h5 class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">${headerText}</h5>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
            ${typeConfig.fields.map((field) => getDynamicFieldHtml(field)).join('')}
        </div>
    `;

    anchor.appendChild(container);

    container.querySelectorAll('input[type="date"], input[type="datetime-local"]')
        .forEach((input) => bindDatePlaceholderColor(input));

    bindNativeFieldBehavior(container);
    mountDynamicSelectControls(container, typeConfig);
}
